import { Component } from '@theme/component';
import { RecentlyViewed, FavoriteProducts, memoryReady, MEMORY_EVENT } from '@theme/recently-viewed-products';

const VIEW_EVENT = 'bp:show-favorites';

/** @extends {Component<{button:HTMLButtonElement,cookies:HTMLButtonElement,label:HTMLElement,view:HTMLAnchorElement,count:HTMLElement,status:HTMLElement,strings:HTMLScriptElement}>} */
class FavoriteProduct extends Component {
  #listeners;
  #strings = {};
  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#listeners?.abort(); }
  async #bind() {
    this.#listeners?.abort();
    const listeners = this.#listeners = new AbortController();
    this.#strings = JSON.parse(this.refs.strings.textContent || '{}');
    document.addEventListener(MEMORY_EVENT, () => this.#update(), { signal: listeners.signal });
    await memoryReady();
    if (listeners.signal.aborted) return;
    this.hidden = false;
    this.#update();
  }
  #update() {
    const ids = FavoriteProducts.getProducts();
    const saved = ids.includes(this.dataset.productId);
    this.refs.button.setAttribute('aria-pressed', String(saved));
    this.refs.label.textContent = this.#strings[saved ? 'saved' : 'save'];
    this.refs.view.hidden = ids.length === 0;
    this.refs.count.textContent = `(${ids.length})`;
    if (FavoriteProducts.allowed()) {
      this.refs.cookies.hidden = true;
      if (this.refs.status.textContent === this.#strings.blocked) this.refs.status.textContent = '';
    }
  }
  toggle() {
    const outcome = FavoriteProducts.toggle(this.dataset.productId);
    this.refs.cookies.hidden = outcome !== 'blocked';
    this.refs.status.textContent = this.#strings[outcome] || this.#strings.unavailable;
    this.#update();
  }
  /** @param {MouseEvent} event */
  viewFavorites(event) { event.preventDefault(); document.dispatchEvent(new CustomEvent(VIEW_EVENT)); }
  async openPreferences() {
    try { await window.privacyBanner.showPreferences(); }
    catch { this.refs.status.textContent = this.#strings.blocked; }
  }
}

/** @extends {Component<{recentButton:HTMLButtonElement,favoriteButton:HTMLButtonElement,recentCount:HTMLElement,favoriteCount:HTMLElement,clear:HTMLButtonElement,status:HTMLElement,products:HTMLUListElement,strings:HTMLScriptElement}>} */
class SavedProducts extends Component {
  #listeners;
  #request;
  #observer;
  #strings = {};
  #mode = 'recent';
  #visible = false;
  #force = false;
  #signature = '';
  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#signature = ''; this.#bind(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort(); this.#request?.abort(); this.#observer?.disconnect();
  }
  async #bind() {
    this.#listeners?.abort(); this.#request?.abort(); this.#observer?.disconnect();
    const listeners = this.#listeners = new AbortController();
    this.#strings = JSON.parse(this.refs.strings.textContent || '{}');
    document.addEventListener(MEMORY_EVENT, () => this.#render(), { signal: listeners.signal });
    document.addEventListener(VIEW_EVENT, async () => {
      this.#visible = true;
      await this.showFavorites();
      if (listeners.signal.aborted || this.#mode !== 'favorites' || this.hidden) return;
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.focus({ preventScroll: true });
    }, { signal: listeners.signal });
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#visible = true; this.#observer.disconnect(); this.#render();
    }, { rootMargin: '200px' });
    await memoryReady();
    if (listeners.signal.aborted) return;
    this.#observer.observe(this);
    this.#render();
  }
  #ids(mode = this.#mode) {
    if (mode === 'favorites') return FavoriteProducts.getProducts();
    return RecentlyViewed.getProducts().filter((id) => id !== this.dataset.currentProduct);
  }
  showRecent() { this.#mode = 'recent'; this.#force = true; this.#render(); }
  showFavorites() { this.#mode = 'favorites'; this.#force = true; return this.#render(); }
  async openPreferences() {
    try { await window.privacyBanner.showPreferences(); }
    catch { this.refs.status.textContent = this.#strings.unavailable; }
  }
  clear() {
    this.#force = true;
    const ok = this.#mode === 'recent' ? RecentlyViewed.clearProducts() : FavoriteProducts.clearProducts();
    if (!ok) this.refs.status.textContent = this.#strings.unavailable;
    this.refs[this.#mode === 'recent' ? 'recentButton' : 'favoriteButton'].focus();
  }
  /** @param {MouseEvent} event */
  handleClick(event) {
    const target = event.composedPath()[0];
    const button = target instanceof Element ? target.closest('button[data-remove]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const outcome = FavoriteProducts.toggle(button.dataset.remove);
    if (outcome === 'removed') this.refs.favoriteButton.focus();
    else this.refs.status.textContent = this.#strings.unavailable;
  }
  async #render() {
    const recent = this.#ids('recent'), favorites = this.#ids('favorites');
    this.hidden = !FavoriteProducts.allowed() || (!this.#force && recent.length + favorites.length === 0);
    this.refs.recentCount.textContent = `(${recent.length})`;
    this.refs.favoriteCount.textContent = `(${favorites.length})`;
    this.refs.recentButton.setAttribute('aria-pressed', String(this.#mode === 'recent'));
    this.refs.favoriteButton.setAttribute('aria-pressed', String(this.#mode === 'favorites'));
    this.refs.clear.textContent = this.#strings[this.#mode === 'recent' ? 'clear_recent' : 'clear_favorites'];
    const ids = this.#ids();
    this.refs.clear.hidden = ids.length === 0;
    const signature = `${this.#mode}:${ids.join(',')}:${FavoriteProducts.allowed()}`;
    if (signature === this.#signature) return;
    this.#request?.abort();
    this.refs.products.replaceChildren();
    if (this.hidden) { this.#signature = ''; return; }
    if (ids.length === 0) {
      this.#signature = signature;
      this.refs.status.textContent = this.#strings[this.#mode === 'recent' ? 'empty_recent' : 'empty_favorites'];
      return;
    }
    if (!this.#visible) return;
    this.#signature = signature;
    const controller = this.#request = new AbortController();
    this.refs.status.textContent = this.#strings.loading;
    try {
      const url = new URL(this.dataset.searchUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid route');
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('section_id', 'bp-saved-product-results');
      const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Product request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || !this.isConnected || !FavoriteProducts.allowed()) return;
      const nodes = new Map([...html.querySelectorAll('[data-memory-product]')].map((node) => [node.dataset.memoryProduct, node]));
      let count = 0;
      for (const id of ids) {
        const node = nodes.get(id);
        if (!node) continue;
        const anchor = node.querySelector('a');
        const destination = new URL(anchor?.getAttribute('href') || '', location.origin);
        if (destination.origin !== location.origin || !/\/products\/[^/]+$/.test(destination.pathname)) continue;
        const item = document.importNode(node, true);
        if (this.#mode === 'favorites') {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'bp-memory__remove'; button.dataset.remove = id;
          button.textContent = this.#strings.remove;
          button.setAttribute('aria-label', `${this.#strings.remove}: ${node.querySelector('h3')?.textContent || ''}`);
          item.append(button);
        }
        this.refs.products.append(item);
        count++;
      }
      this.refs.status.textContent = count < ids.length ? this.#strings.missing : '';
    } catch {
      if (controller.signal.aborted) return;
      this.#signature = '';
      this.refs.status.textContent = this.#strings.error;
    }
  }
}
if (!customElements.get('bp-favorite-product')) customElements.define('bp-favorite-product', FavoriteProduct);
if (!customElements.get('bp-saved-products')) customElements.define('bp-saved-products', SavedProducts);
